import { useState, useEffect, useRef } from 'react';
import { useTheme } from '../context/theme';
import { api } from '../utils/api';
import { renderIcon } from '../utils/icons';

const RECENT_KEY = 'lp-recent-searches';
const MAX_RECENT = 5;

function getRecent() {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; } catch { return []; }
}
function addRecent(q) {
  const list = getRecent().filter(s => s !== q);
  list.unshift(q);
  localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, MAX_RECENT)));
}
function clearRecent() { localStorage.removeItem(RECENT_KEY); }

/* Highlight matching portions in text */
function Highlight({ text, query }) {
  if (!text || !query) return text || '';
  const words = query.toLowerCase().split(/\s+/).filter(w => w.length >= 2);
  if (!words.length) return text;
  const regex = new RegExp('(' + words.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|') + ')', 'gi');
  const parts = text.split(regex);
  return parts.map((part, i) =>
    i % 2 === 1 ? <mark key={i} style={{ background: 'rgba(74,222,128,0.2)', color: 'inherit', borderRadius: 2, padding: '0 1px', fontWeight: 700 }}>{part}</mark> : part
  );
}

/* Score a result title against query for relevance ranking */
function fuzzyScore(title, query) {
  if (!title || !query) return 0;
  const t = title.toLowerCase(), q2 = query.toLowerCase();
  if (t === q2) return 100; // exact match
  if (t.startsWith(q2)) return 80; // starts with
  // Check each word
  const words = q2.split(/\s+/);
  let score = 0;
  for (const w of words) {
    if (t.startsWith(w)) score += 40;
    else if (t.includes(w)) score += 20;
    else {
      // Character-level fuzzy: check if chars appear in order
      let ti = 0, matched = 0;
      for (const c of w) { const found = t.indexOf(c, ti); if (found >= 0) { matched++; ti = found + 1; } }
      score += Math.round((matched / w.length) * 10);
    }
  }
  return score;
}

export default function CmdPalette({ onNav }) {
  const { theme } = useTheme();
  const [open, sO] = useState(false);
  const [q, sQ] = useState('');
  const [res, sR] = useState(null);
  const [ld, sL] = useState(false);
  const [idx, setIdx] = useState(-1);
  const [groupBy, setGroupBy] = useState('type');
  const iRef = useRef(null);
  const dRef = useRef(null);

  useEffect(() => {
    const h = e => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') { e.preventDefault(); sO(o => !o); }
      if (e.key === 'Escape') { sO(false); sQ(''); sR(null); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  useEffect(() => { if (open && iRef.current) iRef.current.focus(); }, [open]);

  useEffect(() => {
    if (!q.trim()) { sR(null); return; }
    clearTimeout(dRef.current);
    dRef.current = setTimeout(() => {
      sL(true);
      api.get('/api/search?q=' + encodeURIComponent(q)).then(d => { sR(d); sL(false); }).catch(() => sL(false));
    }, 300);
    return () => clearTimeout(dRef.current);
  }, [q]);

  useEffect(() => { setIdx(-1); }, [q, res]);

  const go = (v, id) => {
    if (q.trim()) addRecent(q.trim());
    sO(false); sQ(''); sR(null); setIdx(-1);
    if (onNav) onNav(v, id);
  };

  const useRecent = (term) => { sQ(term); };

  if (!open) return null;

  // Build flat list of navigable items for keyboard nav
  const quickActions = [
    { l: 'Dashboard', v: 'dashboard', icon: 'layout-dashboard' },
    { l: 'Pipeline', v: 'pipeline', icon: 'target' },
    { l: 'Sites', v: 'sites', icon: 'map-pin' },
    { l: 'Partners', v: 'partners', icon: 'users' },
    { l: 'Admin', v: 'admin', icon: 'settings' },
  ];

  const rankItems = (items) => items.map(it => ({ ...it, _score: fuzzyScore(it.title, q) })).sort((a, b) => b._score - a._score);

  const typeSections = res ? [
    { key: 'sites', icon: 'map-pin', label: 'Sites', items: rankItems((res.sites || []).map(s => ({ id: s.id, title: s.name || s.address, sub: s.address || '', nav: 'sites', navId: s.id, _typeIcon: 'map-pin' }))) },
    { key: 'items', icon: 'clipboard-list', label: 'Tasks', items: rankItems((res.items || []).map(i => ({ id: i.id, title: i.doc, sub: i.site_name ? (i.site_name + (i.assigned_name ? ' · ' + i.assigned_name : '')) : '', nav: 'sites', navId: i.site_id, _typeIcon: 'clipboard-list', _siteName: i.site_name }))) },
    { key: 'documents', icon: 'file', label: 'Documents', items: rankItems((res.documents || []).map(d => ({ id: d.id, title: d.original_name || d.file_name, sub: (d.site_name || '') + (d.category ? ' · ' + d.category : ''), nav: 'documents', navId: d.site_id, _typeIcon: 'file', _siteName: d.site_name }))) },
    { key: 'partners', icon: 'users', label: 'Partners', items: rankItems((res.partners || []).map(p => ({ id: p.id, title: p.name, sub: p.role || p.email || '', nav: 'partners', navId: p.id, _typeIcon: 'users' }))) },
    { key: 'notes', icon: 'file-text', label: 'Notes', items: rankItems((res.notes || []).map(n => ({ id: n.id, title: n.text?.substring(0, 80) || 'Note', sub: (n.site_name || '') + (n.author_name ? ' · ' + n.author_name : ''), nav: 'sites', navId: n.site_id, _typeIcon: 'file-text', _siteName: n.site_name }))) },
    { key: 'prospects', icon: 'search', label: 'Prospects', items: rankItems((res.prospects || []).map(p => ({ id: p.id, title: p.property_name, sub: p.address || '', nav: 'pipeline', navId: p.id, _typeIcon: 'search' }))) },
    { key: 'users', icon: 'user', label: 'Users', items: rankItems((res.users || []).map(u => ({ id: u.id, title: u.name, sub: u.email || '', nav: 'admin', navId: u.id, _typeIcon: 'user' }))) }
  ].filter(s => s.items.length > 0) : [];

  // Build site-grouped sections
  const siteSections = (() => {
    if (!res || groupBy !== 'site') return [];
    const allItems = typeSections.flatMap(s => s.items);
    const siteGroups = {};
    const otherItems = [];
    for (const it of allItems) {
      if (it._siteName) {
        if (!siteGroups[it._siteName]) siteGroups[it._siteName] = [];
        siteGroups[it._siteName].push(it);
      } else {
        otherItems.push(it);
      }
    }
    const result = Object.keys(siteGroups).sort().map(name => ({
      key: 'site-' + name, icon: 'map-pin', label: name,
      items: siteGroups[name].sort((a, b) => b._score - a._score)
    }));
    if (otherItems.length > 0) {
      result.push({ key: 'site-other', icon: 'folder', label: 'Other', items: otherItems.sort((a, b) => b._score - a._score) });
    }
    return result;
  })();

  const sections = groupBy === 'site' ? siteSections : typeSections;

  // Flat list for arrow key navigation
  const flatItems = res
    ? sections.flatMap(s => s.items.slice(0, 8))
    : quickActions.map(a => ({ nav: a.v, title: a.l }));

  const handleKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setIdx(i => Math.min(i + 1, flatItems.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setIdx(i => Math.max(i - 1, -1)); }
    else if (e.key === 'Enter' && idx >= 0 && flatItems[idx]) { e.preventDefault(); go(flatItems[idx].nav, flatItems[idx].navId); }
  };

  const recent = getRecent();
  const hasResults = sections.length > 0;
  const totalResults = sections.reduce((sum, s) => sum + s.items.length, 0);

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) { sO(false); sQ(''); sR(null); } }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '15vh', zIndex: 9999
      }}
    >
      <div style={{
        background: theme.bgModal, borderRadius: 16, width: '100%', maxWidth: 560, maxHeight: '65vh',
        overflow: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.5)', border: `1px solid ${theme.border}`
      }}>
        {/* Search input */}
        <div style={{ padding: '14px 16px', borderBottom: `1px solid ${theme.border}`, display: 'flex', alignItems: 'center', gap: 10 }}>
          {renderIcon('search', 16, theme.textMuted)}
          <input
            ref={iRef} value={q} onChange={e => sQ(e.target.value)} onKeyDown={handleKey}
            placeholder="Search sites, tasks, documents..."
            style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: theme.text, fontSize: 16, fontFamily: "'DM Sans', sans-serif" }}
          />
          <kbd style={{ fontSize: 10, color: theme.textMuted, background: theme.bgHover, padding: '2px 6px', borderRadius: 4, border: `1px solid ${theme.border}` }}>ESC</kbd>
        </div>

        <div style={{ padding: '6px 0' }}>
          {/* Recent searches — only show when no query */}
          {!q && recent.length > 0 && (
            <div style={{ padding: '4px 16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ color: theme.textMuted, fontSize: 10, textTransform: 'uppercase', letterSpacing: 1 }}>Recent</span>
                <button onClick={clearRecent} style={{ background: 'none', border: 'none', color: theme.textMuted, fontSize: 10, cursor: 'pointer', padding: '2px 4px' }}>Clear</button>
              </div>
              {recent.map((r, i) => (
                <div key={i} onClick={() => useRecent(r)} style={{
                  padding: '5px 12px', borderRadius: 6, cursor: 'pointer', color: theme.textSecondary, fontSize: 13,
                  display: 'flex', alignItems: 'center', gap: 8
                }} onMouseEnter={e => e.currentTarget.style.background = theme.bgHover}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  {renderIcon('clock', 12, theme.textMuted)} {r}
                </div>
              ))}
            </div>
          )}

          {/* Quick actions — show when no query */}
          {!q && (
            <div style={{ padding: '4px 16px' }}>
              <div style={{ color: theme.textMuted, fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4, marginTop: 4 }}>Jump to</div>
              {quickActions.map((a, i) => (
                <div key={a.v} onClick={() => go(a.v)} style={{
                  padding: '7px 12px', borderRadius: 8, cursor: 'pointer', color: idx === i ? theme.text : theme.textSecondary, fontSize: 14,
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: idx === i ? theme.bgHover : 'transparent',
                  fontFamily: "'DM Sans', sans-serif",
                }} onMouseEnter={e => { e.currentTarget.style.background = theme.bgHover; setIdx(i); }}
                  onMouseLeave={e => { if (idx !== i) e.currentTarget.style.background = 'transparent'; }}>
                  {renderIcon(a.icon, 14, idx === i ? theme.accent : theme.textMuted)} {a.l}
                </div>
              ))}
            </div>
          )}

          {/* Loading */}
          {ld && <div style={{ color: theme.textMuted, fontSize: 12, textAlign: 'center', padding: 12 }}>Searching...</div>}

          {/* Results */}
          {res && !ld && (
            hasResults ? (
              <>
                <div style={{ padding: '4px 16px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ color: theme.textMuted, fontSize: 10, textTransform: 'uppercase', letterSpacing: 1 }}>
                    {totalResults} result{totalResults !== 1 ? 's' : ''}
                  </span>
                  <div style={{ display: 'flex', gap: 2 }}>
                    {['type', 'site'].map(mode => (
                      <button key={mode} onClick={() => setGroupBy(mode)} style={{
                        background: groupBy === mode ? theme.accentMuted : 'transparent',
                        color: groupBy === mode ? theme.text : theme.textMuted,
                        border: `1px solid ${groupBy === mode ? theme.accent : theme.border}`,
                        borderRadius: 4, padding: '1px 8px', fontSize: 10, cursor: 'pointer',
                        fontFamily: "'DM Sans', sans-serif", textTransform: 'capitalize',
                      }}>
                        {mode === 'type' ? 'Type' : 'Site'}
                      </button>
                    ))}
                  </div>
                </div>
                {(() => {
                  let flatIdx = 0;
                  return sections.map(s => (
                    <div key={s.key} style={{ padding: '4px 16px' }}>
                      <div style={{ color: theme.textMuted, fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                        {renderIcon(s.icon, 10, theme.textMuted)} {s.label}
                        <span style={{ background: theme.bgHover, padding: '1px 6px', borderRadius: 4, fontSize: 10 }}>{s.items.length}</span>
                      </div>
                      {s.items.slice(0, 8).map((it, i) => {
                        const thisIdx = flatIdx++;
                        return (
                          <div key={i} onClick={() => go(it.nav, it.navId)} style={{
                            padding: '6px 12px', borderRadius: 6, cursor: 'pointer', color: theme.text, fontSize: 13,
                            display: 'flex', alignItems: 'center', gap: 8,
                            background: idx === thisIdx ? theme.accentSubtle : 'transparent',
                            borderLeft: idx === thisIdx ? `2px solid ${theme.accent}` : '2px solid transparent',
                          }} onMouseEnter={e => { e.currentTarget.style.background = theme.accentSubtle; setIdx(thisIdx); }}
                            onMouseLeave={e => { if (idx !== thisIdx) e.currentTarget.style.background = 'transparent'; }}>
                            {groupBy === 'site' && it._typeIcon && (
                              <span style={{ flexShrink: 0, opacity: 0.6 }}>{renderIcon(it._typeIcon, 12, theme.textMuted)}</span>
                            )}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><Highlight text={it.title} query={q} /></div>
                              {it.sub && <div style={{ fontSize: 11, color: theme.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><Highlight text={it.sub} query={q} /></div>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ));
                })()}
              </>
            ) : (
              <div style={{ color: theme.textMuted, fontSize: 13, textAlign: 'center', padding: '20px 12px' }}>
                No results for "{q}"
              </div>
            )
          )}
        </div>

        {/* Footer hint */}
        <div style={{ padding: '8px 16px', borderTop: `1px solid ${theme.border}`, display: 'flex', gap: 12, justifyContent: 'center' }}>
          <span style={{ fontSize: 10, color: theme.textMuted }}>↑↓ navigate</span>
          <span style={{ fontSize: 10, color: theme.textMuted }}>↵ select</span>
          <span style={{ fontSize: 10, color: theme.textMuted }}>esc close</span>
        </div>
      </div>
    </div>
  );
}
